import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { LegacyVoiceOption as VoiceOption } from "@/constants/audioVoice";

const SPEED = { SLOW: "slow", MEDIUM: "medium", FAST: "fast" } as const;
import { useForm } from "@/features/tutor/context/FormContext";
import DefaultVoice from "./DefaultVoice";
import CompactVoice, { AudioState } from "./CompactVoice";

export enum VisualMode {
  DEFAULT = "default",
  COMPACT = "compact",
}

export interface VoicePreviewRef {
  play: () => void;
  pause: () => void;
}

interface VoicePreviewProps {
  voice: VoiceOption | null | undefined;
  visualMode: VisualMode;
  onProgress?: (progress: number) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  isHovered?: boolean;
  externalAudioState?: AudioState;
  onIconClick?: () => void;
  onSpeedChange?: (speed: string) => void;
  defaultSpeed: string;
  speedOptions?: VoiceOption["speedOptions"];
  stackSpeed?: boolean;
}

const VoicePreview = forwardRef<VoicePreviewRef, VoicePreviewProps>(
  (
    {
      voice,
      visualMode,
      onProgress,
      onPlayStateChange,
      isHovered = false,
      externalAudioState,
      onIconClick,
      onSpeedChange,
      defaultSpeed,
      speedOptions,
      stackSpeed = false,
    },
    ref,
  ) => {
    const { formData } = useForm();
    const langCode = formData.language?.value ?? "en";

    const [currentSpeed, setCurrentSpeed] = useState<string>(
      defaultSpeed || "",
    );
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);
    const previousSpeedRef = useRef<string | null>(null);

    useImperativeHandle(ref, () => ({
      play: async () => {
        const audio = audioRef.current;
        if (!audio || !audio.src) return;
        try {
          await audio.play();
        } catch (error) {
          console.error("Error playing audio:", error);
        }
      },
      pause: () => {
        audioRef.current?.pause();
      },
    }));

    useEffect(() => {
      if (defaultSpeed) {
        setCurrentSpeed(defaultSpeed);
      }
    }, [voice?.value, defaultSpeed]);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio || !voice || !currentSpeed) return;

      const basePath = voice.audio[currentSpeed as SPEED];
      if (!basePath) return;

      // For non-English languages, insert lang code before .webm
      // e.g. /voice_options_audio/Oliver.webm -> /voice_options_audio/Oliver_hi.webm
      const s3Base = import.meta.env.VITE_S3_BUCKET_URL;
      if (!s3Base) {
        audio.src = "";
        return;
      }

      const audioPath =
        langCode && langCode !== "en" && langCode !== "auto"
          ? basePath.replace(".webm", `_${langCode}.webm`)
          : basePath;
      const audioUrl = `${s3Base}${audioPath}?v=4`;

      const handleError = () => {
        console.error("Error loading audio:", audioUrl);
        audio.src = "";
        setProgress(0);
        setIsPlaying(false);
        onProgress?.(0);
      };

      const handleCanPlay = () => {
        audio.removeEventListener("error", handleError);
        audio.removeEventListener("canplay", handleCanPlay);
        if (
          previousSpeedRef.current !== null &&
          previousSpeedRef.current !== currentSpeed
        ) {
          audio.play();
        }
        previousSpeedRef.current = currentSpeed;
      };

      audio.addEventListener("error", handleError);
      audio.addEventListener("canplay", handleCanPlay);

      audio.src = audioUrl;
      audio.load();
      setProgress(0);
      setIsPlaying(false);
      onProgress?.(0);

      return () => {
        audio.removeEventListener("error", handleError);
        audio.removeEventListener("canplay", handleCanPlay);
      };
    }, [voice?.value, voice?.audio, currentSpeed, langCode, onProgress]);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const updateProgress = () => {
        if (audio.duration && audio.duration > 0 && !audio.paused) {
          const currentProgress = (audio.currentTime / audio.duration) * 100;
          setProgress(currentProgress);
          onProgress?.(currentProgress);
        }
      };

      const handlePlay = () => {
        setIsPlaying(true);
        onPlayStateChange?.(true);
      };

      const handlePause = () => {
        setIsPlaying(false);
        onPlayStateChange?.(false);
      };

      const handleEnded = () => {
        setProgress(0);
        setIsPlaying(false);
        onProgress?.(0);
        onPlayStateChange?.(false);
      };

      const handleLoadedMetadata = () => {
        updateProgress();
      };

      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }, [onProgress, onPlayStateChange]);

    const togglePlayPause = () => {
      const audio = audioRef.current;
      if (!audio || !audio.src) return;

      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch((error) => {
          console.error("Error playing audio:", error);
        });
      }
    };

    const handleSpeedChange = (newSpeed: string) => {
      setCurrentSpeed(newSpeed);
      onSpeedChange?.(newSpeed);
    };

    if (!voice) return null;

    const getAudioState = (): AudioState => {
      if (externalAudioState !== undefined) {
        return externalAudioState;
      }
      const audio = audioRef.current;
      if (!audio || !audio.src) return AudioState.INITIAL;
      return isPlaying ? AudioState.PLAYING : AudioState.PAUSED;
    };

    switch (visualMode) {
      case VisualMode.DEFAULT:
        return (
          <DefaultVoice
            voice={voice}
            audioRef={audioRef}
            isPlaying={isPlaying}
            currentSpeed={currentSpeed}
            handleSpeedChange={handleSpeedChange}
            togglePlayPause={togglePlayPause}
            progress={progress}
            speedOptions={speedOptions}
            stackSpeed={stackSpeed}
          />
        );
      case VisualMode.COMPACT:
        return (
          <CompactVoice
            voice={voice}
            audioRef={audioRef}
            audioState={getAudioState()}
            isHovered={isHovered}
            onIconClick={onIconClick}
          />
        );
    }
  },
);

VoicePreview.displayName = "VoicePreview";

export default VoicePreview;
